import { Primary } from '@cinch-labs/design-system'
import { useViewport } from '@cinch-labs/shared-util'
import { TrackingEventTypes, useAnalytics } from '@cinch-nx/shared-analytics'
import cx from 'classnames'

import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
} from '../carousel'
import { useFeaturedEVs } from '../hooks/use-featured-evs'
import TitleWithLogo from '../title-with-logo/title-with-logo'
import VehicleCard from '../vehicle-card/vehicle-card'
import styles from './featured-evs-carousel.module.css'

export interface FeaturedEVsCarouselProps {
  headingLeftText?: string
  headingRightText?: string
  headingLogo?: 'cinchShield' | 'cinchCharge' | 'cinchChargePurple'
  browseButtonText: string
  slimMarginBottom?: boolean
}

export const FeaturedEVsCarousel: React.FC<FeaturedEVsCarouselProps> = ({
  headingLeftText,
  headingRightText,
  headingLogo,
  browseButtonText,
  slimMarginBottom = false,
}) => {
  const { data, browseButtonUrl, error } = useFeaturedEVs({})

  const { trackEvent } = useAnalytics()

  const trackingEventCategoryString = `${headingLeftText} ${
    headingLogo === 'cinchChargePurple' ? 'cinchCharge' : headingLogo
  } ${headingRightText}`

  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })
  const isMobile = vw < 1024

  if (!data) {
    return <div>Loading...</div>
  }

  if (error) {
    console.log(error)
    return null
  }

  if (data.vehicleListings.length === 0) {
    return null
  }

  const groupedVehicles = () => {
    let slideSize = 1
    if (vw >= 768) {
      slideSize = 2
    }
    if (vw >= 1024) {
      slideSize = 4
    }
    const vehicles = []
    for (let i = 0; i < data.vehicleListings.length; i += slideSize) {
      const chunk = data.vehicleListings.slice(i, i + slideSize)
      vehicles.push(chunk)
    }
    return vehicles
  }

  const carouselSettings = {
    name: 'featured-evs',
    ariaLabels: {
      previous: 'Go to previous slide',
      next: 'Go to next slide',
    },
    swiperSettings: {
      spaceBetween: 0,
      slidesPerView: 1,
    },
  }

  return (
    <section
      className={cx(styles.container, {
        [styles.slimMarginBottom]: slimMarginBottom,
      })}
    >
      <TitleWithLogo
        headingLevel={'h3'}
        leftText={headingLeftText}
        rightText={headingRightText}
        textClassName={styles.title}
        logo={headingLogo}
        logoHeight={isMobile ? '22' : '36'}
        logoWidth={isMobile ? '117' : '192'}
        wrapperClassName={styles.titleWrapper}
      />

      <Carousel
        name={carouselSettings.name}
        swiperSettings={carouselSettings.swiperSettings}
        customClassNames={{
          carousel: styles.carousel,
        }}
      >
        {groupedVehicles().map((vehicleGroup, outerIndex) => (
          <CarouselSlide key={outerIndex}>
            <div className={styles.cardContainer}>
              {vehicleGroup.map((vehicle) => (
                <VehicleCard
                  key={vehicle.vehicleId}
                  id={vehicle.vehicleId}
                  vehicle={vehicle}
                  isAvailable={vehicle.isAvailable}
                  isSaved={false}
                  isPending={false}
                  discountInPence={vehicle.discountInPence}
                  showMonthlyPrice
                  showActions={false}
                  onCardClick={() => {
                    trackEvent({
                      eventName: `cinchCharge landing page - View vehicle click`,
                      data: {
                        event: {
                          name: 'click',
                          category: 'electric-vehicles',
                          action: 'open',
                          label: vehicle.vehicleId,
                          value: true,
                        },
                      },
                      type: TrackingEventTypes.ADOBE,
                    })
                  }}
                ></VehicleCard>
              ))}
            </div>
          </CarouselSlide>
        ))}
      </Carousel>
      <CarouselControls customClassNames={styles.carouselControls}>
        <CarouselPagination name={carouselSettings.name} />
        <CarouselNavigation
          name={carouselSettings.name}
          ariaLabels={carouselSettings.ariaLabels}
          customClassNames={{
            prevButton: styles.carouselNavButton,
            nextButton: styles.carouselNavButton,
          }}
        />
      </CarouselControls>

      <div className={styles.buttonWrapper}>
        <Primary
          element="a"
          href={browseButtonUrl}
          onClick={() => {
            trackEvent({
              eventName: `cinchCharge landing page - Browse cars click`,
              data: {
                event: {
                  name: 'click',
                  category:
                    trackingEventCategoryString ||
                    'Shop featured cars with cinchCharge',
                  action: 'CTA',
                  label: browseButtonText,
                },
              },
              type: TrackingEventTypes.ADOBE,
            })
          }}
          classNames={{ button: styles.browseCarsButton }}
        >
          {browseButtonText}
        </Primary>
      </div>
    </section>
  )
}
